import Decimal from 'decimal.js-light';
import { formatUnits } from 'viem';

import type { CrossChainQuote } from '@/lib/api/0x-cross-chain/types';
import { tokenFormatter } from '@/lib/formatting';

import { TextSkeleton } from '@/components/Skeleton';

import { CHAIN_IDS } from '@/constants/chain';

import { useSwapFeeQuery } from '@/hooks/transactionModule/useSwapFeeQuery';

import { fontStyles } from '@/styles/typography';

import { InfoCircleSolidSmallIcon } from '@/ui/Icons/InfoCircleSolidSmallIcon';
import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipBottomSheet,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { Fees, Price } from '@/utils/0x.types';
import { isRwaIssuer, type TokenResult } from '@/utils/0x/token-registry.types';
import { isEthereumAddress } from '@/utils/models';
import { normalizeTokenAddress } from '@/utils/normalizeAddress';
import { calculateTotalFeePercent, formatFeePercent } from '@/utils/quote-fees';

import { FeeBreakdown } from './FeeBreakdown';

type ReceiveRecapLineMarketModeProps = {
  isLoading: boolean;
  buyAmount: string | undefined;
  sellAmount: string | undefined;
  buyToken: TokenResult;
  sellToken: TokenResult;
  isGasless: boolean;
  fees: Fees | undefined;
  currentPrice?: Price;
  isStablecoinPair: boolean;
};

export const ReceiveRecapLineMarketMode = ({
  isLoading,
  buyAmount,
  sellAmount,
  buyToken,
  sellToken,
  fees,
  isGasless,
  isStablecoinPair,
}: ReceiveRecapLineMarketModeProps) => {
  const { integratorName, swapFeeBps } = useSwapFeeQuery({
    buyTokenAddress: isEthereumAddress(buyToken.address) ? buyToken.address : undefined,
    buyTokenChainId: buyToken.chainId !== CHAIN_IDS.SOLANA ? buyToken.chainId : undefined,
    sellTokenAddress: isEthereumAddress(sellToken.address) ? sellToken.address : undefined,
    sellTokenChainId: sellToken.chainId !== CHAIN_IDS.SOLANA ? sellToken.chainId : undefined,
  });

  const suppressIntegratorFee =
    isGasless && (isRwaIssuer(sellToken.issuer) || isRwaIssuer(buyToken.issuer));

  const feePercent = calculateTotalFeePercent({
    fees,
    sellToken,
    sellAmount,
    buyAmount,
    suppressIntegratorFee,
  });

  const feeLabel = feePercent !== undefined ? `${formatFeePercent(feePercent)} fee` : undefined;
  const showFeeDetails = isGasless || fees?.zeroExFee || fees?.integratorFee;

  const receiveAmount = buyAmount
    ? tokenFormatter.format(buyAmount, { decimals: buyToken.decimals, symbol: buyToken.symbol })
    : undefined;

  return (
    <div className="flex justify-between py-2.5 px-6">
      <span
        className={fontStyles({
          variant: 'BODY_MD',
          class: 'text-token-gray-800 flex items-center h-6',
        })}
      >
        You receive&nbsp;
        {!isLoading && buyAmount && (
          <>
            {feeLabel && <span>({feeLabel})</span>}
            {showFeeDetails && (
              <MatchaTooltip delayDuration={0}>
                <MatchaTooltipTrigger>
                  <span>
                    <InfoCircleSolidSmallIcon className="text-token-gray-700 ml-1 flex justify-center" />
                  </span>
                </MatchaTooltipTrigger>
                <MatchaTooltipContent>
                  <FeeBreakdown
                    fees={fees}
                    flow={isGasless ? 'intents' : 'swap'}
                    sellToken={sellToken}
                    buyToken={buyToken}
                    sellAmount={sellAmount}
                    buyAmount={buyAmount}
                    swapFeeBps={swapFeeBps}
                    integratorName={integratorName}
                    isStablecoinPair={isStablecoinPair}
                  />
                </MatchaTooltipContent>
                <MatchaTooltipPopover className="py-3 max-w-[318px]" />
                <MatchaTooltipBottomSheet header={isGasless ? 'Gasless fees' : 'Swap fees'} />
              </MatchaTooltip>
            )}
          </>
        )}
      </span>

      <div
        data-testid="receive-amount"
        className={fontStyles({
          variant: 'BODY_MD',
          class: 'text-token-gray-1600 flex items-center',
        })}
      >
        {isLoading || !receiveAmount ? <TextSkeleton width={100} height={14} /> : receiveAmount}
      </div>
    </div>
  );
};

type ReceiveRecapLineCrossChainModeProps = {
  isLoading: boolean;
  quote: CrossChainQuote;
  buyToken: TokenResult;
};

const getCrossChainFeePercentLabel = (quote: CrossChainQuote): string => {
  const { fees, sellAmount, steps } = quote;
  if (!fees.integratorFee) return '0% fee';

  const { amount, token, type } = fees.integratorFee;
  if (BigInt(amount) <= 0n) return '0% fee';

  // Relay may collect the fee in the intermediate token entering the bridge after an
  // origin-chain swap, so match that token to its route amount before calculating the percentage.
  const isFeeToken = (step: CrossChainQuote['steps'][number]): boolean =>
    normalizeTokenAddress(step.sellToken, step.originChainId) ===
    normalizeTokenAddress(token, step.originChainId);

  const originStep = steps[0];
  const bridgeStep = steps.find((step) => step.type === 'bridge' && isFeeToken(step));
  const feeVolumeAmount =
    originStep && isFeeToken(originStep) ? sellAmount : bridgeStep?.sellAmount;
  if (type !== 'volume' || !feeVolumeAmount) return 'incl. fee';

  const volumeAmount = new Decimal(feeVolumeAmount);
  if (volumeAmount.lte(0)) return 'incl. fee';

  const feePercent = new Decimal(amount).div(volumeAmount).mul(100).toNumber();
  const feePercentText = parseFloat(feePercent.toFixed(2)).toString();

  return `${feePercentText}% fee`;
};

export const ReceiveRecapLineCrossChainMode = ({
  isLoading,
  quote,
  buyToken,
}: ReceiveRecapLineCrossChainModeProps) => {
  const feeLabel = getCrossChainFeePercentLabel(quote);
  const formattedBuyAmount = tokenFormatter.format(
    formatUnits(BigInt(quote.buyAmount), buyToken.decimals),
    { symbol: buyToken.symbol },
  );

  return (
    <div className="flex justify-between py-2.5 px-6">
      <span
        className={fontStyles({
          variant: 'BODY_MD',
          class: 'text-token-gray-800 flex items-center h-6',
        })}
      >
        You receive ({feeLabel})
      </span>
      <div
        data-testid="receive-amount"
        className={fontStyles({
          variant: 'BODY_MD',
          class: 'text-token-gray-1600 flex items-center',
        })}
      >
        {isLoading ? <TextSkeleton width={100} height={14} /> : formattedBuyAmount}
      </div>
    </div>
  );
};
